/**
 * Lightweight, dependency-free fuzzy matcher used by the Settings ⌘+K
 * palette (#1319). Returns a numeric score (higher = better) or 0 when the
 * needle doesn't match. Treat any positive score as a hit.
 *
 * Algorithm:
 *   1. Substring hit (case-insensitive) scores highest — full credit per
 *      character, with a startsWith bonus.
 *   2. Acronym / character-skip hit ("ntf" matches "Notifications") scores
 *      lower — characters of `needle` must appear in `haystack` in order
 *      but not contiguously. Earlier matches and word-boundary matches
 *      score better.
 *   3. Whitespace-tokenized split — every whitespace-separated token in
 *      `needle` must independently match somewhere in `haystack`. Score
 *      is the sum across tokens.
 *
 * The intent is "fast and good-enough" — not a clone of Fuse / fzy. For
 * 24-ish settings labels + descriptions, the linear scan is fine.
 */

export interface FuzzyMatchOptions {
  /**
   * Optional weight for the haystack — useful when scoring multiple
   * fields (label vs. description vs. keywords) so a hit on the label
   * outranks a hit deep in the description.
   */
  weight?: number;
}

/**
 * Match a single needle token against a haystack. Returns a positive
 * score on hit, 0 on miss.
 */
function scoreToken(needle: string, haystack: string): number {
  if (!needle) return 0;
  if (!haystack) return 0;
  const n = needle.toLowerCase();
  const h = haystack.toLowerCase();

  // Substring hit
  const idx = h.indexOf(n);
  if (idx !== -1) {
    let s = 100 + n.length * 10;
    if (idx === 0) s += 50;
    // Word-boundary bonus: matched substring sits right after a space.
    else if (h[idx - 1] === ' ' || h[idx - 1] === '-' || h[idx - 1] === '/') s += 25;
    return s;
  }

  // Character-skip / acronym match — every char of `n` appears in `h`
  // in order. Score boosts when matches land on word boundaries.
  let hi = 0;
  let matched = 0;
  let boundaryBonus = 0;
  for (let ni = 0; ni < n.length; ni++) {
    const ch = n[ni];
    let found = -1;
    while (hi < h.length) {
      if (h[hi] === ch) { found = hi; hi++; break; }
      hi++;
    }
    if (found === -1) return 0;
    matched++;
    if (found === 0 || h[found - 1] === ' ' || h[found - 1] === '-' || h[found - 1] === '/') {
      boundaryBonus += 10;
    }
  }
  if (matched !== n.length) return 0;
  // Penalize "loose" matches where lots of chars were skipped.
  const density = matched / Math.max(1, hi);
  return Math.round(20 + matched * 5 + boundaryBonus + density * 30);
}

/**
 * Score one haystack against a (possibly multi-token) needle. Every
 * whitespace-separated token in `needle` must hit; the final score is the
 * sum.
 */
export function fuzzyScore(
  needle: string,
  haystack: string,
  options: FuzzyMatchOptions = {},
): number {
  const trimmed = needle.trim();
  if (!trimmed) return 0;
  const tokens = trimmed.split(/\s+/);
  let total = 0;
  for (const t of tokens) {
    const s = scoreToken(t, haystack);
    if (s === 0) return 0;
    total += s;
  }
  return Math.round(total * (options.weight ?? 1));
}

/**
 * Score across multiple weighted haystacks for the same item. Returns the
 * sum of positive token-scores per haystack. A miss on any token in any
 * single haystack does not kill the result — different fields are
 * independent searches; the best (any-hit) score wins.
 */
export function fuzzyScoreFields(
  needle: string,
  fields: { value: string; weight?: number }[],
): number {
  const trimmed = needle.trim();
  if (!trimmed) return 0;
  let best = 0;
  for (const f of fields) {
    const s = fuzzyScore(needle, f.value, { weight: f.weight });
    if (s > best) best = s;
  }
  return best;
}
